import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Mail, ArrowLeft, CheckCircle2, ImageOff, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const FILTERS = [
  ['all', 'All eligible'],
  ['not_sent', 'Not yet emailed'],
  ['no_image', 'No photo/logo'],
  ['incomplete', 'Incomplete profile'],
];

const AdminFeaturedRollout = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sel, setSel] = useState({});
  const [template, setTemplate] = useState('listing_live');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === 'all' ? '' : `?filter=${filter}`;
      const r = await axios.get(`${API_URL}/admin/featured/rollout/candidates${q}`);
      setRows(r.data.candidates || []);
    } catch { toast.error('Could not load candidates'); } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const toggle = (k) => setSel((s) => ({ ...s, [k]: !s[k] }));
  const selectedKeys = Object.keys(sel).filter((k) => sel[k]);
  const selectAll = () => {
    const all = {}; rows.forEach((r) => { all[r.key] = true; }); setSel(all);
  };

  const send = async () => {
    if (!selectedKeys.length) { toast.error('Select at least one listing'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/admin/featured/rollout/send`, { keys: selectedKeys, template });
      toast.success(`Sent ${r.data.sent}, failed ${r.data.failed}`);
      setSel({}); await load();
    } catch (e) { toast.error('Send failed'); } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16" data-testid="rollout-page">
      <section className="bg-primary pt-24 pb-6 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Link to="/admin/featured" className="text-secondary text-sm hover:underline flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Featured Program</Link>
          <h1 className="font-serif text-3xl font-bold mt-2 flex items-center gap-2"><Mail className="w-6 h-6 text-secondary" /> Existing-User Rollout</h1>
          <p className="text-white/80 mt-1 text-sm">Invite existing listings to update their profile and claim complimentary Featured placement.</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-4">
        <div className="bg-white rounded-xl border p-4 flex flex-wrap items-center gap-2">
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${filter === k ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'}`}>{label}</button>
          ))}
          <div className="flex-1" />
          <select value={template} onChange={(e) => setTemplate(e.target.value)} data-testid="template-select" className="border rounded-lg px-2 py-1.5 text-sm">
            <option value="listing_live">"Your Listing Is Live" (invite + 30-day Featured)</option>
            <option value="featured">"You're Being Featured" (congrats)</option>
          </select>
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all">Select all</Button>
          <Button className="bg-secondary text-white" size="sm" disabled={sending || !selectedKeys.length} onClick={send} data-testid="send-rollout">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send ({selectedKeys.length})</>}
          </Button>
        </div>

        <div className="bg-white rounded-xl border divide-y" data-testid="candidates-list">
          {loading ? (
            <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-gray-400 text-sm">No listings match this filter.</p>
          ) : rows.map((r) => (
            <label key={r.key} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50" data-testid={`cand-${r.key}`}>
              <input type="checkbox" checked={!!sel[r.key]} onChange={() => toggle(r.key)} className="w-4 h-4" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{r.name} <span className="text-gray-400 font-normal">· {r.email}</span></p>
                <p className="text-xs text-gray-500 truncate capitalize">{r.listing_type.replace('_', ' ')} · {r.listing_title}</p>
              </div>
              {!r.has_image && <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1"><ImageOff className="w-3 h-3" /> No image</Badge>}
              {r.email_status === 'sent' && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1"><CheckCircle2 className="w-3 h-3" /> {r.opened ? 'Opened' : 'Sent'}</Badge>}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminFeaturedRollout;
